import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import * as StyledForm from "./Styled.Form";
import useForm from "@/hooks/useForm";
import useEmail from "@/hooks/useEmail";
import { useLocation } from "react-router-dom";
import { useTheme } from "styled-components";
import { FormConfig, getConfigForPath } from "./formConfig";

const EmailSignup = () => {
  const { values, updateValue } = useForm({
    email: "",
  });

  const { email } = values;

  const location = useLocation();
  const currentPath = location.pathname;

  // default: 2 columns on large viewport
  const defaultCols = ["$gForm2Col"];

  const config = getConfigForPath(location.pathname, FormConfig, {
    title: "Start here, today",
    description:
      "This is where it all begins. Receive weekly insights you can instantly apply to your business and life.",
    cols: defaultCols, // <-- 2-col by default
    formId: "8565264", // <-- must exist
  });

  const { message, loading, error, submitEmail } = useEmail({
    values,
    formId: config.formId, // Brevo list ID
    extraData: {
      source: currentPath, // automatically track current page
    },
  });

  const formRef = useRef(null);
  const inputRef = useRef(null);
  const labelAnimation = useRef(null);

  useGSAP(
    () => {
      const allLabels = document.querySelectorAll(".label");
      const allText = document.querySelectorAll(".text");

      labelAnimation.current = gsap.timeline({
        paused: true,
        defaults: {
          ease: "power3.inOut",
        },
      });

      labelAnimation.current
        .to(allLabels, {
          duration: 0.15,
          scale: 0.75,
          yPercent: -50,
          x: "-24",
        })
        .to(
          allText,
          {
            duration: 0.15,
            color: "#ED2835",
          },
          0
        )
        .to(
          allText,
          {
            duration: 0.15,
            css: {
              padding: "0 8",
            },
          },
          0
        );

      inputRef.current.addEventListener("focus", function (event) {
        console.log("Input focused");
        labelAnimation.current.play();
      });
      inputRef.current.addEventListener("blur", function (event) {
        console.log("Input blurred");
        if (!inputRef.current.value) {
          console.log("Input blurred. Value:", inputRef.current.value);
          labelAnimation.current.reverse();
        }
      });
    },
    { scope: formRef }
  );

  const theme = useTheme();
  console.log("EmailSignup rendered");

  return (
    <StyledForm.GridFormWrapper>
      <StyledForm.GridItemFormWrapper>
        <StyledForm.FormCard as="form" ref={formRef} onSubmit={submitEmail}>
          <fieldset disabled={loading}>
            <StyledForm.ModulePin as="legend">
              Try my newsletter
            </StyledForm.ModulePin>
            <StyledForm.GridForm $cols={config.cols}>
              {/* title */}
              <StyledForm.GridItemForm area="title">
                <StyledForm.FormTitle>{config.title}</StyledForm.FormTitle>
              </StyledForm.GridItemForm>

              {/* description */}
              <StyledForm.GridItemForm area="description">
                <p>{config.description}</p>
              </StyledForm.GridItemForm>
              <StyledForm.GridItemForm area="input">
                <StyledForm.TextFieldWrapper>
                  <input
                    type="email"
                    name="email"
                    id={`email-${Math.random().toString(36).substring(2, 15)}`}
                    onChange={updateValue}
                    value={email}
                    aria-labelledby="label-email"
                    ref={inputRef}
                  />
                  <label className="label" htmlFor="name" id="label-email">
                    <StyledForm.FormInputText className="text">
                      Your email address
                    </StyledForm.FormInputText>
                  </label>
                </StyledForm.TextFieldWrapper>
              </StyledForm.GridItemForm>
              <StyledForm.GridItemForm area="button">
                <StyledForm.FormButton
                  type="submit"
                  as="button"
                  disabled={loading}
                >
                  {loading ? "Subscribing..." : " Subscribe"}
                </StyledForm.FormButton>
              </StyledForm.GridItemForm>
            </StyledForm.GridForm>
          </fieldset>
          {message ? (
            <OutcomeMessageContainer
              error={error}
              message={message}
              theme={theme}
            />
          ) : (
            ""
          )}
        </StyledForm.FormCard>
      </StyledForm.GridItemFormWrapper>
      <StyledForm.GridItemFormWrapper>
        <small>
          Actionable tips and insights right to your inbox weekly. It’s free.
          Unsubscribe anytime.
        </small>
      </StyledForm.GridItemFormWrapper>
    </StyledForm.GridFormWrapper>
  );
};

export default EmailSignup;

const OutcomeMessageContainer = ({ error, message, theme }) => {
  const messageContent =
    typeof message === "object" && message?.type === "error" ? (
      <span style={{ color: theme.colors.imperialRed }}>{message.text}</span>
    ) : (
      message
    );

  return (
    <StyledForm.MessageContainer>
      <StyledForm.FormMessageStyle>
        {messageContent}
      </StyledForm.FormMessageStyle>
    </StyledForm.MessageContainer>
  );
};
